import { useDataRoots } from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import { detailOf, statusOf } from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import {
  useSaveWorkspaceMounts,
  useWorkspaceMounts,
} from "@via/pages/agent-workspaces/workspaces.api.tsx";
import {
  isSameOrBelow,
  validateMountContainerPath,
  validateMountHostPath,
  type MountMode,
  type MountSpec,
  type RunEnvironmentDataRoot,
} from "common";
import { type JSX, useEffect, useState } from "react";

export const RW_DISABLED_HINT = "This folder's allowed folder is read only";

const inputClass =
  "block w-full rounded-md border-0 px-2 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600";
const secondaryButton =
  "rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton =
  "rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Whether a mount at `hostPath` may be read and write: the longest allowed folder that holds it
 * decides, as the server does. Two cases are left to the server, which checks every save: the
 * allowed folders could not be read here (only the environment's owner may list them, so a person
 * using a shared environment cannot), and a read and write folder written under `~`, which only the
 * agent can resolve.
 */
export const rwAllowedFor = (
  hostPath: string,
  roots: RunEnvironmentDataRoot[] | undefined
): boolean => {
  if (roots === undefined) return true;
  if (validateMountHostPath(hostPath).kind !== "ok") return false;
  const holding = roots
    .filter(root => root.path.startsWith("/"))
    .filter(root => isSameOrBelow(hostPath, root.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (holding) return holding.mode === "read_write";
  return roots.some(
    root => !root.path.startsWith("/") && root.mode === "read_write"
  );
};

/** The first thing wrong with each row, by index, or an empty object when every row passes. */
export const checkMountRows = (
  rows: MountSpec[],
  roots: RunEnvironmentDataRoot[] | undefined
): Record<number, string> => {
  const problems: Record<number, string> = {};
  rows.forEach((row, index) => {
    const host = validateMountHostPath(row.hostPath);
    if (host.kind === "refused") {
      problems[index] = host.detail;
      return;
    }
    const container = validateMountContainerPath(row.containerPath);
    if (container.kind === "refused") {
      problems[index] = container.detail;
      return;
    }
    if (row.mode === "rw" && !rwAllowedFor(row.hostPath, roots)) {
      problems[index] = `${RW_DISABLED_HINT}: mount it read only.`;
    }
  });
  return problems;
};

/** The server's refusal of a save, in words, naming the mount it is about when it names one. */
const saveErrorText = (error: unknown): string => {
  const status = statusOf(error);
  const data = (error as { response?: { data?: { index?: number | null } } })
    ?.response?.data;
  const detail = detailOf(error);
  if (status === 409) {
    return (
      detail ??
      "Someone changed these mounts since you opened them. They were reloaded; make your change again."
    );
  }
  if (status === 404)
    return "This workspace was not found, or it is not yours.";
  if (detail && typeof data?.index === "number") {
    return `Mount ${data.index + 1}: ${detail}`;
  }
  return detail ?? "The mounts could not be saved.";
};

const MODE_TEXT: Readonly<Record<MountMode, string>> = {
  ro: "Read only",
  rw: "Read and write",
};

/**
 * The host folders a workspace's jobs see (spec tier 2). A new row is read only; read and write is
 * offered only under a read and write allowed folder. Save replaces the whole list at the version it
 * was read at, so two editors cannot overwrite each other silently.
 */
export function MountsEditor({
  workspaceId,
  profileClusterId,
}: {
  workspaceId: string;
  profileClusterId: number | null;
}): JSX.Element {
  const mounts = useWorkspaceMounts(workspaceId);
  const roots = useDataRoots(profileClusterId ?? undefined);
  const save = useSaveWorkspaceMounts(workspaceId);
  const [rows, setRows] = useState<MountSpec[]>([]);
  const [problems, setProblems] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (mounts.data) setRows(mounts.data.mounts.map(row => ({ ...row })));
  }, [mounts.data]);

  // A roots read that failed (a person using a shared environment gets a 403) leaves the rule to
  // the server; a read still loading does too, until it lands.
  const knownRoots = roots.isSuccess ? roots.data : undefined;

  if (mounts.isLoading) {
    return <p className="text-sm text-gray-600">Loading mounts</p>;
  }
  if (mounts.isError || !mounts.data) {
    return (
      <p className="text-sm text-gray-600">
        This workspace&apos;s mounts could not be loaded.
      </p>
    );
  }
  const version = mounts.data.version;

  const update = (index: number, change: Partial<MountSpec>): void => {
    setSaved(false);
    setRows(current =>
      current.map((row, at) => {
        if (at !== index) return row;
        const next = { ...row, ...change };
        // A host path moved out from under a read and write folder falls back to read only.
        if (next.mode === "rw" && !rwAllowedFor(next.hostPath, knownRoots)) {
          next.mode = "ro";
        }
        return next;
      })
    );
  };

  const onSave = async (): Promise<void> => {
    setError(null);
    setSaved(false);
    const found = checkMountRows(rows, knownRoots);
    setProblems(found);
    if (Object.keys(found).length > 0) return;
    try {
      await save.mutateAsync({ version, mounts: rows });
      setSaved(true);
    } catch (failure) {
      setError(saveErrorText(failure));
      // A stale version: reload the list so the next save names the current one.
      if (statusOf(failure) === 409) void mounts.refetch();
    }
  };

  return (
    <section className="space-y-3" aria-labelledby="workspace-mounts-heading">
      <h2
        id="workspace-mounts-heading"
        className="text-base font-semibold text-gray-900">
        Mounts
      </h2>
      <p className="text-sm text-gray-600">
        Folders on the cluster that this workspace&apos;s jobs see, at{" "}
        <span className="font-mono">/mnt/&lt;name&gt;</span>. A new mount is
        read only.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-600">No mounts yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row, index) => {
            const number = index + 1;
            const rwAllowed = rwAllowedFor(row.hostPath, knownRoots);
            return (
              <li
                key={index}
                className="grid gap-2 rounded-md border border-gray-200 p-3 sm:grid-cols-[2fr_1fr_auto_auto]">
                <input
                  aria-label={`Host path of mount ${number}`}
                  className={`${inputClass} font-mono`}
                  placeholder="/data/lab/project"
                  value={row.hostPath}
                  onChange={event =>
                    update(index, { hostPath: event.target.value })
                  }
                />
                <input
                  aria-label={`Container path of mount ${number}`}
                  className={`${inputClass} font-mono`}
                  placeholder="/mnt/project"
                  value={row.containerPath}
                  onChange={event =>
                    update(index, { containerPath: event.target.value })
                  }
                />
                <select
                  aria-label={`Mode of mount ${number}`}
                  className={inputClass}
                  value={row.mode}
                  onChange={event =>
                    update(index, { mode: event.target.value as MountMode })
                  }>
                  <option value="ro">{MODE_TEXT.ro}</option>
                  <option value="rw" disabled={!rwAllowed}>
                    {MODE_TEXT.rw}
                  </option>
                </select>
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`Remove mount ${number}`}
                  onClick={() => {
                    setSaved(false);
                    setProblems({});
                    setRows(current => current.filter((_, at) => at !== index));
                  }}>
                  Remove
                </button>
                {!rwAllowed && row.hostPath.trim() !== "" ? (
                  <p className="text-xs text-gray-500 sm:col-span-4">
                    {RW_DISABLED_HINT}
                  </p>
                ) : null}
                {problems[index] ? (
                  <p
                    className="text-xs text-red-600 sm:col-span-4"
                    role="alert">
                    {problems[index]}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? <p className="text-sm text-green-700">Mounts saved.</p> : null}
      <div className="flex gap-2">
        <button
          type="button"
          className={secondaryButton}
          onClick={() => {
            setSaved(false);
            setRows(current => [
              ...current,
              { hostPath: "", containerPath: "", mode: "ro" },
            ]);
          }}>
          Add mount
        </button>
        <button
          type="button"
          className={primaryButton}
          disabled={save.isPending}
          onClick={onSave}>
          Save mounts
        </button>
      </div>
    </section>
  );
}
